import { css } from 'aphrodite';
import {
	Dialog,
	DialogPopup,
	DialogHeader,
	DialogTitle,
	DialogBody,
	DialogFooter,
} from '../../../components-v2/Dialog';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { styleSheet } from './styles/DeleteStepConfirmationDialogStyles';

interface DeleteStepConfirmationDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	/** Number of steps that will be removed. */
	count: number;
	/** Label of the single step being removed, when exactly one is targeted. */
	primaryLabel?: string;
}

export function DeleteStepConfirmationDialog({
	isOpen,
	onClose,
	onConfirm,
	count,
	primaryLabel,
}: DeleteStepConfirmationDialogProps) {
	const isSingle = count <= 1;
	const title = isSingle ? 'Delete step?' : `Delete ${count} steps?`;
	const description = isSingle
		? `This removes ${primaryLabel ? `“${primaryLabel}”` : 'this step'} from your automation.`
		: `This removes ${count} steps from your automation.`;
	// Deletions live in the unpublished draft, so they're reversible — but only by discarding the
	// whole draft, which also throws away every other unpublished edit. Make that trade-off explicit.
	const calloutText = `You can undo this with Discard changes, but that reverts all unpublished edits since your last publish — not just ${
		isSingle ? 'this step' : 'these steps'
	}.`;
	const confirmLabel = isSingle ? 'Delete step' : 'Delete steps';

	return (
		<Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<p className={css(styleSheet.description)}>{description}</p>
					<div className={css(styleSheet.warningBox)}>
						<svg
							width='16'
							height='16'
							viewBox='0 0 16 16'
							fill='none'
							className={css(styleSheet.warningIcon)}
							aria-hidden='true'
						>
							<path
								d='M8 1L15 14H1L8 1Z'
								stroke={THEME.colors.warning.dark}
								strokeWidth='1.5'
								strokeLinecap='round'
								strokeLinejoin='round'
								fill='none'
							/>
							<path d='M8 6v3' stroke={THEME.colors.warning.dark} strokeWidth='1.5' strokeLinecap='round' />
							<circle cx='8' cy='11.5' r='0.75' fill={THEME.colors.warning.dark} />
						</svg>
						<span>{calloutText}</span>
					</div>
				</DialogBody>
				<DialogFooter styles={[styleSheet.actions]}>
					<Button variant='secondary' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='destructive' onClick={onConfirm}>
						{confirmLabel}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
